export const serviceUmbrellas = [
  {
    slug: "managed-services",
    badge: "MANAGED",
    icon: "Settings",
    title: "Managed Services",
    hook: "Keep Your Endpoint Environment Running at Its Best",
    tagline: "Keep devices secure and compliant",
    paragraphs: [
      "Technology doesn't stop after deployment. Devices require ongoing management, regular maintenance, security updates, and operational support to remain reliable throughout their lifecycle.",
      "Our Managed Services provide continuous endpoint administration designed to keep your devices secure, compliant, and ready for work, so your team can focus on the business while we handle the day-to-day management.",
      "Whether you need complete endpoint management or support for specific operational functions, our services scale alongside your business and help ensure a consistent experience for every employee.",
    ],
    services: [
      {
        slug: "device-management",
        icon: "Laptop",
        title: "Device Management",
        hook: "Complete Endpoint Management Without the Day-to-Day Burden",
        teaser:
          "Comprehensive day-to-day administration of your endpoint environment. We manage devices throughout their lifecycle, helping maintain security, compliance, application management, and overall device health.",
        paragraphs: [
          "Empower your workforce with reliable, secure, and fully managed devices, without the burden of day-to-day administration. We provide ongoing management of your organization's Windows and macOS devices, helping ensure they remain secure, compliant, up to date, and ready for work throughout their entire lifecycle.",
          "Whether you're supporting a growing team or an established organization, we handle the operational work behind the scenes so your employees stay productive while you focus on running your business.",
        ],
        howWeHelp: [
          "Manage your endpoint environment through centralized administration and ongoing oversight.",
          "Maintain device security and compliance to help protect your business and meet organizational standards.",
          "Deploy applications and configuration updates to keep users productive and devices consistent.",
          "Support employee onboarding and offboarding with secure device preparation and management.",
          "Monitor device health and performance to identify issues before they impact users.",
          "Manage the hardware lifecycle from deployment through replacement and retirement.",
        ],
        idealFor: [
          "Small and medium-sized businesses",
          "Organizations without dedicated endpoint administrators",
          "Businesses looking to outsource day-to-day device management",
          "Companies standardizing Windows and macOS management",
        ],
        related: ["patch-management", "device-onboarding", "technology-refresh"],
      },
      {
        slug: "patch-management",
        icon: "ShieldCheck",
        title: "Patch Management",
        hook: "Keep Your Devices Secure, Stable, and Up to Date",
        teaser:
          "Keep operating systems and third-party applications current with ongoing patch management designed to improve security, reduce vulnerabilities, and maintain system reliability.",
        paragraphs: [
          "Effective patch management is essential to maintaining a secure and reliable technology environment. We provide ongoing management of operating system and application updates, helping reduce security risks, improve stability, and ensure your devices remain current with minimal disruption.",
          "Through proactive monitoring and routine maintenance, we help keep your endpoint environment protected and performing at its best.",
        ],
        howWeHelp: [
          "Deploy operating system updates to improve security and system reliability.",
          "Maintain third-party applications with current patches and security updates.",
          "Monitor update compliance to help ensure devices remain protected.",
          "Identify and resolve update failures before they become larger issues.",
          "Schedule maintenance activities to minimize disruption to your users.",
          "Provide visibility into patch status to support informed technology decisions.",
        ],
        idealFor: [
          "Organizations requiring ongoing update management",
          "Businesses focused on improving security",
          "Companies without internal patch management resources",
        ],
        related: ["device-management", "device-onboarding"],
      },
      {
        slug: "provisioning-deployment",
        icon: "Boxes",
        title: "Provisioning & Deployment",
        hook: "Business-Ready Devices Whenever You Need Them",
        teaser:
          "Provide employees with business-ready devices whenever they're needed. From new hires to replacement hardware, we prepare, configure, and deploy devices that are ready for productivity from day one.",
        paragraphs: [
          "Preparing and deploying new devices doesn't have to slow down your business. Our ongoing provisioning and deployment service ensures every device is configured, secured, and ready for work before it reaches your employees.",
          "Whether you're onboarding new hires, replacing aging hardware, or supporting your organization's continued growth, we deliver a consistent deployment experience that helps reduce downtime, improve productivity, and simplify endpoint operations.",
        ],
        howWeHelp: [
          "Prepare business-ready devices configured to your organization's standards.",
          "Deploy operating systems and business applications required for your users.",
          "Apply security settings and organizational policies to help protect devices from day one.",
          "Support ongoing employee onboarding with devices ready for immediate productivity.",
          "Coordinate device deployments as your organization grows and technology needs evolve.",
          "Maintain consistent deployment standards to deliver the same experience across every device.",
        ],
        idealFor: [
          "Organizations with recurring employee onboarding",
          "Businesses without dedicated desktop deployment staff",
          "Growing companies expanding their workforce",
          "Organizations seeking a consistent device deployment process",
          "Businesses outsourcing endpoint deployment operations",
        ],
        related: [
          "device-management",
          "patch-management",
          "device-onboarding",
          "technology-refresh",
        ],
      },
    ],
  },
  {
    slug: "project-services",
    badge: "PROJECT",
    icon: "Rocket",
    title: "Project Services",
    hook: "Move Every Stage of the Endpoint Lifecycle Forward",
    tagline: "Deploy, migrate, refresh, and retire",
    paragraphs: [
      "Technology evolves, and so should your devices. Whether you're deploying new hardware, transitioning to modern device management, refreshing aging equipment, or securely retiring obsolete assets, our project services help organizations navigate every stage of the endpoint lifecycle with confidence.",
      "From onboarding and provisioning to migrations, refreshes, and secure disposal, we deliver projects that minimize disruption while preparing your business for what's next.",
    ],
    services: [
      {
        slug: "device-onboarding",
        icon: "UserPlus",
        title: "Device Onboarding",
        hook: "Bring Your Devices Under Modern Management",
        teaser:
          "Enroll devices into a cloud-based management platform for centralized administration and security.",
        paragraphs: [
          "Modern device management starts with a strong foundation. We enroll your organization's devices into a cloud-based management platform, providing centralized administration, enhanced security, and greater visibility across your environment from day one.",
          "Whether you're implementing cloud device management for the first time or expanding your existing environment, we help ensure every device is ready for ongoing management.",
        ],
        howWeHelp: [
          "Enroll devices into your management platform for centralized administration.",
          "Configure devices for organizational use based on your business requirements.",
          "Apply foundational security settings to support a secure deployment.",
          "Validate enrolled devices before they are placed into production.",
          "Establish a consistent management experience across your endpoint environment.",
        ],
        idealFor: [
          "Organizations adopting cloud device management",
          "Businesses deploying newly purchased devices",
          "Companies modernizing endpoint administration",
        ],
        related: ["device-management", "device-provisioning", "device-migration"],
      },
      {
        slug: "device-migration",
        icon: "ArrowRightLeft",
        title: "Device Migration",
        hook: "Transition to a Modern Management Platform with Confidence",
        teaser:
          "Transition devices from one management solution to another with minimal disruption.",
        paragraphs: [
          "Migrating to a new endpoint management solution doesn't have to disrupt your business. We help organizations transition devices between management platforms through careful planning, structured execution, and thorough validation to ensure a smooth experience for both administrators and end users.",
        ],
        howWeHelp: [
          "Develop a migration strategy tailored to your organization's goals.",
          "Transition devices to a new management platform with minimal disruption.",
          "Preserve user productivity throughout the migration process.",
          "Migrate applications and device configurations to support a seamless experience.",
          "Validate migrated devices before project completion.",
        ],
        idealFor: [
          "Organizations replacing an existing management solution",
          "Businesses modernizing endpoint management",
          "Companies transitioning to cloud-based administration",
        ],
        related: ["device-onboarding", "device-management", "technology-refresh"],
      },
      {
        slug: "device-provisioning",
        icon: "PackageCheck",
        title: "Device Provisioning",
        hook: "Deliver Business-Ready Devices from Day One",
        teaser:
          "Prepare business-ready devices configured for your users and your environment.",
        paragraphs: [
          "Give your employees a device that's ready to work the moment they sign in. We prepare and configure business devices to your organization's standards, ensuring every deployment is secure, consistent, and ready for immediate productivity.",
          "Whether you're deploying a handful of devices or supporting a company-wide rollout, we streamline the provisioning process to reduce deployment time and deliver a consistent experience for every user.",
        ],
        howWeHelp: [
          "Prepare business-ready devices configured to your organization's standards.",
          "Deploy operating systems and business applications required for your users.",
          "Apply security settings and organizational policies to help protect devices from day one.",
          "Standardize deployments for a consistent experience across your organization.",
          "Validate every device before deployment to reduce setup issues.",
        ],
        idealFor: [
          "New employee deployments",
          "Business device rollouts",
          "Standardized workstation deployments",
          "Growing organizations",
        ],
        related: ["device-onboarding", "technology-refresh", "device-management"],
      },
      {
        slug: "technology-refresh",
        icon: "RefreshCw",
        title: "Technology Refresh",
        hook: "Modernize Your Hardware Without Disrupting Your Business",
        teaser:
          "Replace aging hardware with modern devices to improve performance, security, and productivity.",
        paragraphs: [
          "Outdated hardware can reduce productivity, increase support costs, and create security risks. We help organizations replace aging devices with modern, business-ready hardware and current operating systems, ensuring employees have reliable technology that supports today's workplace.",
          "From planning and deployment to user transition, we manage every stage of the refresh process to minimize downtime and deliver a seamless experience.",
        ],
        howWeHelp: [
          "Plan your hardware refresh to support an organized and efficient deployment.",
          "Replace aging devices with modern, business-ready hardware.",
          "Transfer user data and business applications to minimize disruption.",
          "Deploy current operating systems that support today's security and productivity requirements.",
          "Coordinate device replacements for a smooth transition.",
          "Validate every deployment before devices are placed into service.",
        ],
        idealFor: [
          "Hardware replacement initiatives",
          "Windows operating system upgrades",
          "Company-wide refresh projects",
          "Organizations standardizing their endpoint environment",
        ],
        related: ["device-provisioning", "hardware-disposal", "device-management"],
      },
      {
        slug: "hardware-disposal",
        icon: "Recycle",
        title: "Hardware Disposal",
        hook: "Retire Technology Securely and Responsibly",
        teaser:
          "Retire obsolete devices securely and responsibly while protecting sensitive business data.",
        paragraphs: [
          "When business devices reach the end of their lifecycle, proper disposal is just as important as deployment. We help organizations retire obsolete hardware securely while protecting sensitive information and supporting environmentally responsible disposal practices.",
          "Whether you're replacing a handful of devices or completing a large-scale refresh, we help ensure retired equipment is handled safely and responsibly.",
        ],
        howWeHelp: [
          "Retire obsolete devices through a structured disposal process.",
          "Protect sensitive business information with secure data handling practices.",
          "Support asset inventory reconciliation during hardware retirement.",
          "Coordinate environmentally responsible disposal through approved recycling methods.",
          "Help complete the hardware lifecycle with confidence and accountability.",
        ],
        idealFor: [
          "Technology refresh projects",
          "Businesses retiring aging hardware",
          "Organizations replacing large numbers of devices",
        ],
        related: ["technology-refresh", "device-provisioning"],
      },
    ],
  },
  {
    slug: "technology-consulting",
    badge: "CONSULTING",
    icon: "Compass",
    title: "Technology Consulting",
    hook: "Make Technology Decisions with Confidence",
    tagline: "Strategic guidance for smarter technology",
    paragraphs: [
      "Choosing the right technology is just as important as implementing it correctly. Our technology consulting services help organizations evaluate their current environment, identify opportunities for improvement, and develop practical solutions that align with their business goals.",
      "Whether you're planning a new initiative, modernizing your endpoint environment, or preparing for future growth, we provide expert guidance to help you make informed technology decisions with confidence.",
    ],
    services: [
      {
        slug: "technology-assessments",
        icon: "ClipboardCheck",
        title: "Technology Assessments",
        hook: "Understand Your Technology Before Planning What's Next",
        teaser:
          "Evaluate your current endpoint infrastructure, management processes, and operational workflows to identify strengths, uncover challenges, and find practical improvements.",
        paragraphs: [
          "A successful technology strategy begins with understanding your current environment. We evaluate your existing endpoint infrastructure, management processes, and operational workflows to identify strengths, uncover challenges, and recommend practical improvements that support your business objectives.",
          "Whether you're planning an upgrade or simply looking for a second opinion, our assessments provide the clarity needed to make informed decisions.",
        ],
        howWeHelp: [
          "Evaluate your current endpoint environment to identify opportunities for improvement.",
          "Review device management processes to improve efficiency and consistency.",
          "Assess security and operational practices to help reduce risk.",
          "Identify technology gaps that may impact productivity or future growth.",
          "Provide practical recommendations tailored to your business goals.",
        ],
        idealFor: [
          "Businesses planning technology improvements",
          "Organizations evaluating their current environment",
          "Companies preparing for modernization initiatives",
        ],
        related: ["endpoint-strategy", "technology-roadmaps", "device-management"],
      },
      {
        slug: "endpoint-strategy",
        icon: "Target",
        title: "Endpoint Strategy",
        hook: "Build a Strong Foundation for Modern Device Management",
        teaser:
          "Develop practical endpoint management plans that improve security, simplify administration, and support long-term growth.",
        paragraphs: [
          "Effective endpoint management requires more than the right tools. It requires the right strategy. We work with organizations to develop practical endpoint management plans that improve security, simplify administration, and support long-term growth.",
          "Whether you're adopting modern management for the first time or refining an existing environment, we help build a strategy that aligns with your operational needs.",
        ],
        howWeHelp: [
          "Develop an endpoint management strategy aligned with your business objectives.",
          "Recommend modern management solutions based on your environment.",
          "Plan standardized device management processes that support long-term growth.",
          "Identify opportunities to improve security and operational efficiency.",
          "Create a roadmap for future endpoint initiatives.",
        ],
        idealFor: [
          "Organizations adopting modern endpoint management",
          "Businesses planning long-term IT improvements",
          "Companies standardizing endpoint operations",
        ],
        related: ["device-onboarding", "device-migration", "device-management"],
      },
      {
        slug: "technology-roadmaps",
        icon: "Map",
        title: "Technology Roadmaps",
        hook: "Plan for Tomorrow Without Disrupting Today",
        teaser:
          "Build technology roadmaps that prioritize initiatives, improve budgeting, and provide a clear path toward modernization.",
        paragraphs: [
          "Technology investments should support your business both today and in the future. We help organizations develop technology roadmaps that prioritize initiatives, improve budgeting, and provide a clear path toward modernization.",
          "Our recommendations focus on practical improvements that align with your operational goals while minimizing unnecessary complexity.",
        ],
        howWeHelp: [
          "Evaluate current technology initiatives to identify future priorities.",
          "Develop phased implementation plans that reduce operational disruption.",
          "Recommend technology improvements based on business objectives.",
          "Identify opportunities to modernize endpoint management.",
          "Support long-term planning through practical technology guidance.",
        ],
        idealFor: [
          "Growing organizations",
          "Businesses planning future technology investments",
          "Companies modernizing their IT environment",
        ],
        related: ["technology-assessments", "endpoint-strategy", "technology-refresh"],
      },
      {
        slug: "technology-procurement-guidance",
        icon: "ShoppingCart",
        title: "Technology Procurement Guidance",
        hook: "Invest in Technology That Supports Your Business",
        teaser:
          "Rather than focusing on specific vendors, we help you make informed purchasing decisions based on your business needs.",
        paragraphs: [
          "Choosing the right hardware and technology solutions can have a lasting impact on productivity, security, and long-term costs. We provide independent guidance to help organizations select technology that aligns with their operational requirements and future growth.",
          "Rather than focusing on specific vendors, we help you make informed purchasing decisions based on your business needs.",
        ],
        howWeHelp: [
          "Recommend hardware solutions that support your business objectives.",
          "Evaluate technology options based on performance, scalability, and long-term value.",
          "Support technology planning before major investments.",
          "Help standardize hardware selections across your organization.",
          "Align purchasing decisions with future endpoint management goals.",
        ],
        idealFor: [
          "Hardware refresh initiatives",
          "Growing organizations",
          "Businesses standardizing their technology environment",
        ],
        related: [
          "technology-refresh",
          "device-provisioning",
          "technology-roadmaps",
        ],
      },
    ],
  },
];

export const flattenServices = (umbrellas) =>
  (umbrellas || []).flatMap((umbrella) =>
    (umbrella.services || []).map((service) => ({ umbrella, service }))
  );

export const allServiceSlugs = flattenServices(serviceUmbrellas).map(
  ({ service }) => service.slug
);
